import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthProvider'
import { useEntitlements } from './useEntitlements'
import { HardTimeoutError, runWithHardTimeout } from '@core/sync/timeout'
import { formatSyncErrorDetails } from '@core/sync/errorFormat'
import { useWriter } from '@/hooks/useWriter'
import { classifySyncStatusMessage, type SyncLastOutcome } from '@/features/sync/syncUiState'
import type {
  SyncActiveAction,
  SyncExecutionPlan,
  SyncPreflightReport,
  SyncScope,
  SyncTriggerSource,
  SyncUiPhase,
} from '@/features/sync/types'

const LAST_SYNC_AT_KEY = 'writers_lens_last_sync_at'
const SYNC_ENABLED_KEY = 'writers_lens_sync_enabled'
const SYNC_ENABLED_EVENT = 'wrlens:sync-enabled-changed'
const MSG_SYNC_FAST_OK = 'Sync completada sin riesgos (no fue necesario abrir Sync segura).'
const MSG_SYNC_WARN_METADATA_CONTINUED = 'Metadata phases timed out; content push continued.'
const MSG_SYNC_DONE_WARNINGS = 'Sync completada con advertencias.'
const MSG_SYNC_WARN_ASSETS_PENDING = 'La imagen no se pudo subir a Supabase; el texto sí se ha sincronizado.'
const MSG_SYNC_SESSION_EXPIRED = 'Necesitas volver a iniciar sesión.'
const MSG_SYNC_NOOP_REMOTE_UNVERIFIED = 'No hay cambios locales; no se aplicó nada.'
const MSG_SYNC_REMOTE_PULL_RECOVERY = 'Sync completada en modo recuperación remota.'

function dispatchSyncProgress(stage: string, detail?: Record<string, unknown>) {
  try { window.dispatchEvent(new CustomEvent('wrlens:sync-progress', { detail: { stage, ...(detail ?? {}) } })) } catch {}
}

function readSyncEnabled(): boolean {
  try { return localStorage.getItem(SYNC_ENABLED_KEY) === '1' } catch { return false }
}

function broadcastSyncEnabled(enabled: boolean) {
  try { window.dispatchEvent(new CustomEvent(SYNC_ENABLED_EVENT, { detail: { enabled } })) } catch {}
}

function countLocalDirty(report: Pick<SyncPreflightReport, 'localDirty'>): number {
  return (report.localDirty?.versions ?? 0)
    + (report.localDirty?.sections ?? 0)
    + (report.localDirty?.contents ?? 0)
}

export function shouldAttemptRemotePullRecovery(report: Pick<SyncPreflightReport, 'remoteVerification' | 'localDirty'>): boolean {
  const localDirtyCount = countLocalDirty(report)
  return report.remoteVerification === 'unverified' && localDirtyCount === 0
}

export function shouldAutoPullSafely(report: Pick<SyncPreflightReport, 'remoteVerification' | 'localDirty' | 'risks'>): boolean {
  const localDirtyCount = countLocalDirty(report)
  return report.remoteVerification === 'verified'
    && localDirtyCount === 0
    && report.risks.every((risk) => !risk.requiresDecision)
}

function normalizeSyncError(error: unknown, report?: SyncPreflightReport): string {
  const raw = formatSyncErrorDetails(error)
  if (/SESSION_EXPIRED|SESSION_UNAVAILABLE/i.test(raw)) {
    return MSG_SYNC_SESSION_EXPIRED
  }
  if (/LOCAL_SAVE_FLUSH_FAILED/i.test(raw)) {
    return 'No se pudieron guardar los cambios locales antes de sincronizar. Reintenta en unos segundos.'
  }
  if (/NOOP_REMOTE_UNVERIFIED/i.test(raw)) {
    return MSG_SYNC_NOOP_REMOTE_UNVERIFIED
  }
  if (/pull incompleto/i.test(raw)) {
    return 'Pull incompleto: no se aplicaron cambios remotos. Reintenta “Sync segura”.'
  }
  if (error instanceof HardTimeoutError) {
    return 'La sincronización excedió el tiempo de espera. Reintenta en unos segundos.'
  }
  if (/network|failed to fetch|load failed|fetch/i.test(raw.toLowerCase())) {
    return 'Error de red con Supabase. Verifica conexión y reintenta.'
  }
  if (/bucket not found/i.test(raw.toLowerCase())) {
    return 'Falta configurar el bucket "assets" en Supabase Storage.'
  }
  if (report?.backendHealth && report.backendHealth !== 'ok') {
    return 'Sync en modo degradado: el backend remoto no respondió de forma estable.'
  }
  return raw
}

export function useSupabaseSync() {
  const { user, isBetaAllowed, refreshProfilePlan } = useAuth()
  const { canUseCloudSync } = useEntitlements()
  const { activeProject, flushPendingSave } = useWriter()
  const [enabled, setEnabledState] = useState<boolean>(() => readSyncEnabled())
  const [syncing, setSyncing] = useState<boolean>(false)
  const [syncPhase, setSyncPhase] = useState<SyncUiPhase>('idle')
  const [activeAction, setActiveAction] = useState<SyncActiveAction>(null)
  const [lastSyncAt, setLastSyncAt] = useState<string | undefined>(() => {
    try { return localStorage.getItem(LAST_SYNC_AT_KEY) ?? undefined } catch { return undefined }
  })
  const [lastSyncMessage, setLastSyncMessage] = useState<string | undefined>(undefined)
  const [lastSyncOutcome, setLastSyncOutcome] = useState<SyncLastOutcome>(null)
  const [syncStatusMessage, setSyncStatusMessage] = useState<string | undefined>(undefined)
  const [lastPreflight, setLastPreflight] = useState<SyncPreflightReport | undefined>(undefined)
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true
  const [lastError, setLastError] = useState<string | undefined>(undefined)
  const blockedByBeta = !isBetaAllowed
  const blockedByPlan = !canUseCloudSync
  const activeProjectId = activeProject?.id
  const activeScope = useMemo<SyncScope | null>(() => {
    if (!activeProjectId) return null
    return {
      mode: 'active_project',
      projectIds: [activeProjectId],
    }
  }, [activeProjectId])
  const hasSyncScope = Boolean(activeScope?.projectIds?.length)
  const ensureCloudSyncEntitlement = useCallback(async (): Promise<boolean> => {
    if (canUseCloudSync) return true
    const refreshedPlan = await refreshProfilePlan()
    return refreshedPlan === 'premium'
  }, [canUseCloudSync, refreshProfilePlan])

  const resetRuntimeState = useCallback((message?: string) => {
    setSyncing(false)
    setSyncPhase('idle')
    setActiveAction(null)
    setSyncStatusMessage(message)
    setLastSyncMessage(undefined)
    setLastSyncOutcome(null)
    setLastPreflight(undefined)
    setLastError(undefined)
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next)
    try { localStorage.setItem(SYNC_ENABLED_KEY, next ? '1' : '0') } catch {}
    broadcastSyncEnabled(next)
  }, [])

  const setPhase = useCallback((phase: SyncUiPhase, trigger: SyncTriggerSource, message?: string) => {
    setSyncPhase(phase)
    if (message) setSyncStatusMessage(message)
    dispatchSyncProgress('sync:phase', { phase, trigger, message })
  }, [])

  const openSyncCenter = useCallback((report?: SyncPreflightReport, trigger: SyncTriggerSource = 'manual_sync_safe') => {
    try { window.dispatchEvent(new CustomEvent('wrlens:sync-center-open', { detail: { report, trigger } })) } catch {}
  }, [])

  const flushEditorStateBeforeSync = useCallback(async () => {
    try {
      await runWithHardTimeout(
        () => flushPendingSave(),
        {
          timeoutMs: 8_000,
          label: 'sync.flushPendingSave',
        },
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      throw new Error(`LOCAL_SAVE_FLUSH_FAILED: ${message}`)
    }
  }, [flushPendingSave])

  const prepareSync = useCallback(async (scope?: SyncScope): Promise<SyncPreflightReport | null> => {
    if (!user) return null
    const effectiveScope = scope ?? activeScope ?? undefined
    const { prepareSync } = await import('@core/sync/safe')
    const report = await prepareSync(user.id, {
      scopeMode: effectiveScope?.mode ?? 'all_projects',
      projectIds: effectiveScope?.projectIds,
    })
    setLastPreflight(report)
    return report
  }, [user, activeScope])

  const executeSafeSync = useCallback(async (plan: SyncExecutionPlan, preflightOverride?: SyncPreflightReport): Promise<void> => {
    if (!user) return
    const { executeSafeSync } = await import('@core/sync/safe')
    const report = preflightOverride ?? lastPreflight ?? await (await import('@core/sync/safe')).prepareSync(user.id, {
      scopeMode: plan.scopeMode ?? (plan.projectIds?.length ? 'active_project' : 'all_projects'),
      projectIds: plan.projectIds,
    })
    await executeSafeSync(user.id, report, plan)
  }, [user, lastPreflight])

  const syncNow = useCallback(async (trigger: SyncTriggerSource = 'manual_sync_now') => {
    if (!user || !enabled || !online || blockedByBeta) return
    if (!(await ensureCloudSyncEntitlement())) return
    if (syncing) return
    if (!activeScope?.projectIds?.length) {
      const msg = 'Abre un proyecto para sincronizar.'
      setLastError(msg)
      setSyncStatusMessage(msg)
      dispatchSyncProgress('sync:error', { error: msg, trigger })
      return
    }

    setSyncing(true)
    setActiveAction('sync_now')
    setLastError(undefined)
    setLastSyncMessage(undefined)
    setPhase('starting', trigger, 'Iniciando Sync ahora…')
    dispatchSyncProgress('sync:start', { trigger })

    try {
      setPhase('starting', trigger, 'Guardando cambios locales…')
      await flushEditorStateBeforeSync()
      const result = await runWithHardTimeout(async () => {
        setPhase('preflight', trigger, 'Analizando cambios y riesgos…')
        const report = await prepareSync(activeScope)
        if (!report) throw new Error('No hay sesión de usuario activa para sincronizar.')

        if (shouldAttemptRemotePullRecovery(report)) {
          setPhase('executing', trigger, 'Intentando recuperar cambios remotos…')
          const plan: SyncExecutionPlan = {
            createdAt: new Date().toISOString(),
            trigger,
            strategy: 'pull_remote_only',
            continueOnProjectUpsertFailure: false,
            scopeMode: activeScope.mode,
            projectIds: activeScope.projectIds,
            resolutionsByRiskId: {},
            createLocalSnapshot: false,
            createCloudSnapshot: false,
          }
          await executeSafeSync(plan, report)
          return { mode: 'pull_only', report } as const
        }

        if (!report.canFastSync) {
          const degradedMsg = report.remoteVerification === 'unverified'
            ? 'No se pudo verificar remoto. Puedes reintentar o subir local con revisión.'
            : report.backendHealth && report.backendHealth !== 'ok'
            ? 'Backend degradado: se abrió Sync segura para revisión manual.'
            : 'Se detectaron riesgos: se abrió Sync segura para revisión manual.'
          setSyncStatusMessage(degradedMsg)
          openSyncCenter(report, trigger)
          return { mode: 'safe', report } as const
        }

        setPhase('executing', trigger, 'Sincronizando cambios…')
        const plan: SyncExecutionPlan = {
          createdAt: new Date().toISOString(),
          trigger,
          continueOnProjectUpsertFailure: false,
          scopeMode: activeScope.mode,
          projectIds: activeScope.projectIds,
          resolutionsByRiskId: {},
          createLocalSnapshot: true,
          createCloudSnapshot: false,
        }
        await executeSafeSync(plan, report)
        return { mode: 'fast', report } as const
      }, {
        timeoutMs: 45_000,
        label: 'syncNow.total',
      })

      if (result.mode === 'fast') {
        const nowIso = new Date().toISOString()
        setLastSyncAt(nowIso)
        setLastSyncMessage(MSG_SYNC_FAST_OK)
        setLastSyncOutcome('fast_ok')
        setPhase('done', trigger, 'Sync completada sin riesgos.')
        dispatchSyncProgress('sync:done', { report: { preflight: result.report }, trigger })
      } else if (result.mode === 'pull_only') {
        const nowIso = new Date().toISOString()
        setLastSyncAt(nowIso)
        setLastSyncMessage(MSG_SYNC_REMOTE_PULL_RECOVERY)
        setLastSyncOutcome('remote_pull_recovery')
        setPhase('done', trigger, 'Recuperación remota completada.')
      } else {
        setPhase('done', trigger, 'Safe sync lista para revisión manual.')
        dispatchSyncProgress('sync:safe-opened', { report: { preflight: result.report }, trigger })
      }
    } catch (e) {
      const msg = normalizeSyncError(e, lastPreflight)
      setLastError(msg)
      setPhase('error', trigger, msg)
      dispatchSyncProgress('sync:error', { error: msg, trigger })
    } finally {
      setSyncing(false)
      setActiveAction(null)
    }
  }, [
    user,
    enabled,
    online,
    blockedByBeta,
    syncing,
    ensureCloudSyncEntitlement,
    flushEditorStateBeforeSync,
    prepareSync,
    openSyncCenter,
    executeSafeSync,
    lastPreflight,
    setPhase,
    activeScope,
  ])

  const startSafeSync = useCallback(async (trigger: SyncTriggerSource = 'manual_sync_safe') => {
    if (!user || !enabled || !online || blockedByBeta) return
    if (!(await ensureCloudSyncEntitlement())) return
    if (syncing) return
    if (!activeScope?.projectIds?.length) {
      const msg = 'Abre un proyecto para sincronizar.'
      setLastError(msg)
      setSyncStatusMessage(msg)
      dispatchSyncProgress('sync:error', { error: msg, trigger })
      return
    }

    setSyncing(true)
    setActiveAction('safe_sync')
    setLastError(undefined)
    setPhase('starting', trigger, 'Iniciando Sync segura…')
    dispatchSyncProgress('sync:start', { trigger, mode: 'safe' })

    try {
      setPhase('starting', trigger, 'Guardando cambios locales…')
      await flushEditorStateBeforeSync()
      const report = await runWithHardTimeout(async () => {
        setPhase('preflight', trigger, 'Analizando conflictos y estado remoto…')
        const preflight = await prepareSync(activeScope)
        if (!preflight) throw new Error('No hay sesión de usuario activa para sincronizar.')
        return preflight
      }, {
        timeoutMs: 45_000,
        label: 'safeSync.preflight',
      })

      openSyncCenter(report, trigger)
      const modeMsg = shouldAttemptRemotePullRecovery(report)
        ? 'No se pudo verificar remoto. Puedes intentar recuperar cambios remotos.'
        : report.remoteVerification === 'unverified'
        ? 'No se pudo verificar remoto. Revisa opciones antes de ejecutar.'
        : report.backendHealth && report.backendHealth !== 'ok'
        ? 'Análisis listo (modo degradado). Revisa antes de ejecutar.'
        : 'Análisis listo. Revisa y ejecuta Sync segura.'
      setPhase('done', trigger, modeMsg)
      dispatchSyncProgress('sync:safe-opened', { report: { preflight: report }, trigger })
    } catch (e) {
      const msg = normalizeSyncError(e, lastPreflight)
      setLastError(msg)
      setPhase('error', trigger, msg)
      dispatchSyncProgress('sync:error', { error: msg, trigger })
    } finally {
      setSyncing(false)
      setActiveAction(null)
    }
  }, [
    user,
    enabled,
    online,
    blockedByBeta,
    syncing,
    ensureCloudSyncEntitlement,
    flushEditorStateBeforeSync,
    prepareSync,
    openSyncCenter,
    lastPreflight,
    setPhase,
    activeScope,
  ])

  const runAutoSyncCheck = useCallback(async (trigger: SyncTriggerSource) => {
    if (!user || !enabled || !online || blockedByBeta || blockedByPlan || syncing || !activeScope?.projectIds?.length) return
    dispatchSyncProgress('sync:auto-check:start', { trigger, projectId: activeScope.projectIds[0] })

    try {
      await flushEditorStateBeforeSync()
      const report = await prepareSync(activeScope)
      if (!report) return
      const localDirtyCount = countLocalDirty(report)
      if (localDirtyCount > 0) {
        const message = 'Hay cambios locales pendientes. Revisa Sync segura antes de bajar remoto.'
        setSyncStatusMessage(message)
        dispatchSyncProgress('sync:auto-pull:skipped', { trigger, reason: 'local_dirty', message })
        return
      }
      if (report.remoteVerification !== 'verified') {
        const message = 'Remoto no verificado. Revisa Sync segura antes de bajar cambios.'
        setSyncStatusMessage(message)
        dispatchSyncProgress('sync:auto-pull:skipped', { trigger, reason: 'remote_unverified', message })
        return
      }
      if (!shouldAutoPullSafely(report)) {
        const message = 'Hay diferencias entre local y remoto. Abre Sync segura para decidir qué conservar.'
        setSyncStatusMessage(message)
        dispatchSyncProgress('sync:auto-pull:skipped', { trigger, reason: 'decision_required', message })
        return
      }

      setSyncing(true)
      setActiveAction(null)
      setLastError(undefined)
      setPhase('executing', trigger, 'Actualizando cambios remotos del proyecto…')
      dispatchSyncProgress('sync:auto-pull:executing', { trigger, projectId: activeScope.projectIds[0] })
      const plan: SyncExecutionPlan = {
        createdAt: new Date().toISOString(),
        trigger,
        strategy: 'pull_remote_only',
        continueOnProjectUpsertFailure: false,
        scopeMode: activeScope.mode,
        projectIds: activeScope.projectIds,
        resolutionsByRiskId: {},
        createLocalSnapshot: false,
        createCloudSnapshot: false,
      }
      await executeSafeSync(plan, report)
      const nowIso = new Date().toISOString()
      setLastSyncAt(nowIso)
      setLastSyncMessage(MSG_SYNC_REMOTE_PULL_RECOVERY)
      setLastSyncOutcome('remote_pull_recovery')
      setSyncStatusMessage(MSG_SYNC_REMOTE_PULL_RECOVERY)
      setPhase('done', trigger, 'Proyecto actualizado desde remoto.')
      dispatchSyncProgress('sync:auto-pull:done', { trigger, projectId: activeScope.projectIds[0] })
    } catch (e) {
      const msg = normalizeSyncError(e, lastPreflight)
      setLastError(msg)
      setSyncStatusMessage(msg)
      setPhase('error', trigger, msg)
      dispatchSyncProgress('sync:auto-pull:error', { trigger, error: msg })
    } finally {
      setSyncing(false)
      setActiveAction(null)
      dispatchSyncProgress('sync:auto-check:done', { trigger, projectId: activeScope.projectIds[0] })
    }
  }, [
    user,
    enabled,
    online,
    blockedByBeta,
    blockedByPlan,
    syncing,
    activeScope,
    flushEditorStateBeforeSync,
    prepareSync,
    executeSafeSync,
    setPhase,
    lastPreflight,
  ])

  useEffect(() => {
    if ((blockedByBeta || blockedByPlan) && enabled) {
      if (blockedByPlan && !blockedByBeta) {
        void refreshProfilePlan()
          .then((plan) => {
            if (plan !== 'premium') setEnabled(false)
          })
          .catch(() => setEnabled(false))
        return
      }
      setEnabled(false)
    }
  }, [blockedByBeta, blockedByPlan, enabled, refreshProfilePlan, setEnabled])

  useEffect(() => {
    const syncFromStorage = () => {
      const next = readSyncEnabled()
      setEnabledState((current) => current === next ? current : next)
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key && event.key !== SYNC_ENABLED_KEY) return
      syncFromStorage()
    }
    const onLocalEvent = () => {
      syncFromStorage()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(SYNC_ENABLED_EVENT, onLocalEvent as EventListener)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(SYNC_ENABLED_EVENT, onLocalEvent as EventListener)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      resetRuntimeState(undefined)
    }
  }, [user, resetRuntimeState])

  useEffect(() => {
    try {
      if (lastSyncAt) localStorage.setItem(LAST_SYNC_AT_KEY, lastSyncAt)
      else localStorage.removeItem(LAST_SYNC_AT_KEY)
    } catch {}
  }, [lastSyncAt])

  useEffect(() => {
    const onSignedOut = () => {
      resetRuntimeState('Sesión cerrada. Vuelve a iniciar sesión para sincronizar.')
      dispatchSyncProgress('sync:reset', { reason: 'auth:signed-out' })
    }
    window.addEventListener('auth:signed-out', onSignedOut)
    return () => window.removeEventListener('auth:signed-out', onSignedOut)
  }, [resetRuntimeState])

  useEffect(() => {
    const onProgress = (event: Event) => {
      const detail = (event as CustomEvent).detail as { stage?: string; message?: string; warningCount?: number } | undefined
      if (!detail?.stage) return
      if (detail.stage === 'sync:warning') {
        const warningMessage = detail.message === MSG_SYNC_WARN_METADATA_CONTINUED
          ? MSG_SYNC_WARN_METADATA_CONTINUED
          : detail.message === MSG_SYNC_WARN_ASSETS_PENDING
          ? MSG_SYNC_WARN_ASSETS_PENDING
          : MSG_SYNC_DONE_WARNINGS
        setLastSyncMessage(warningMessage)
        setLastSyncOutcome(
          warningMessage === MSG_SYNC_WARN_METADATA_CONTINUED
            ? 'warn_metadata_phase'
            : warningMessage === MSG_SYNC_WARN_ASSETS_PENDING
            ? 'warn_assets_pending'
            : 'done_with_warnings',
        )
        setSyncStatusMessage(warningMessage)
      }
      if (detail.stage === 'sync:degraded:push-only:done') {
        const nowIso = new Date().toISOString()
        setLastSyncAt(nowIso)
        if ((detail.warningCount ?? 0) > 0) {
          setLastSyncMessage(MSG_SYNC_DONE_WARNINGS)
          setLastSyncOutcome('done_with_warnings')
          setSyncStatusMessage(MSG_SYNC_DONE_WARNINGS)
        }
      }
      if (detail.stage === 'sync:degraded:pull-only:done') {
        const nowIso = new Date().toISOString()
        setLastSyncAt(nowIso)
        setLastSyncMessage(MSG_SYNC_REMOTE_PULL_RECOVERY)
        setLastSyncOutcome('remote_pull_recovery')
        setSyncStatusMessage(MSG_SYNC_REMOTE_PULL_RECOVERY)
      }
      if (detail.stage === 'sync:auto-pull:skipped') {
        const message = typeof detail.message === 'string' ? detail.message : 'Auto-sync omitida por política.'
        setSyncStatusMessage(message)
      }
      if (detail.stage === 'sync:degraded:noop') {
        setLastSyncMessage(MSG_SYNC_NOOP_REMOTE_UNVERIFIED)
        setLastSyncOutcome('noop_remote_unverified')
        setSyncStatusMessage(MSG_SYNC_NOOP_REMOTE_UNVERIFIED)
      }
    }
    window.addEventListener('wrlens:sync-progress', onProgress)
    return () => window.removeEventListener('wrlens:sync-progress', onProgress)
  }, [])

  const syncStatusKind = useMemo(
    () => classifySyncStatusMessage(syncStatusMessage),
    [syncStatusMessage],
  )

  return useMemo(() => ({
    enabled,
    setEnabled,
    syncing,
    syncPhase,
    activeAction,
    hasSyncScope,
    syncStatusMessage,
    syncStatusKind,
    lastSyncAt,
    lastPreflight,
    syncNow,
    startSafeSync,
    online,
    lastError,
    lastSyncMessage,
    lastSyncOutcome,
    blockedByBeta,
    blockedByPlan,
    prepareSync,
    executeSafeSync,
    runAutoSyncCheck,
    openSyncCenter,
  }), [
    enabled,
    setEnabled,
    syncing,
    syncPhase,
    activeAction,
    hasSyncScope,
    syncStatusMessage,
    syncStatusKind,
    lastSyncAt,
    lastPreflight,
    syncNow,
    startSafeSync,
    online,
    lastError,
    lastSyncMessage,
    lastSyncOutcome,
    blockedByBeta,
    blockedByPlan,
    prepareSync,
    executeSafeSync,
    runAutoSyncCheck,
    openSyncCenter,
  ])
}
